import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Home,
  LayoutDashboard,
  Library,
  LogIn,
  Mail,
  LogOut,
  MoreHorizontal,
  School,
  Scale,
  Shield,
  User,
  Users,
  CalendarDays,
} from "lucide-react";
import ContextSwitcher from "@/components/ContextSwitcher";
import { useAuth, type NavItem } from "@/contexts/AuthContext";
import useCoursePageScopes, { type CoursePageScope, type CoursePageScopes } from "@/hooks/useCoursePageScopes";
import ProposalNavCount, { useProposalNavCounts, type ProposalNavCounts } from "@/components/courses/ProposalNavCount";
import { useUnreadNavCount } from "@/components/messages/unreadNavCount";
import useSchoolPageNav, { type SchoolPageNav } from "@/hooks/useSchoolPageNav";
import useKickHomeNotice from "@/hooks/useKickHomeNotice";
import useMediaQuery from "@/hooks/useMediaQuery";
import "./AppShell.css";

const ICON_MAP = {
  home: Home,
  dashboard: LayoutDashboard,
  login: LogIn,
  profile: User,
  school: School,
  schools: School,
  messages: Mail,
  teach: GraduationCap,
  classes: GraduationCap,
  courses: BookOpen,
  "book-open": BookOpen,
  library: Library,
  "my-students": Users,
  "role-defaults": Shield,
  "grade-rubrics": Scale,
  events: CalendarDays,
} as const;

const PUBLIC_NAV: NavItem[] = [
  { id: "home", label: "Home", path: "/" },
  { id: "login", label: "Login", path: "/auth" },
];

const DOCK_MAX = 5;
const COMPACT_QUERY = "(max-width: 768px)";

function NavIcon({ name }: { name?: string }) {
  const key = String(name || "")
    .trim()
    .toLowerCase() as keyof typeof ICON_MAP;
  const Icon = ICON_MAP[key] || LayoutDashboard;
  return <Icon className="al-shell-nav-icon" aria-hidden="true" strokeWidth={2} />;
}

const CATALOG_SCOPE_ID = "catalog";
const MY_COURSES_SCOPE_ID = "mine";
const OTHERS_SCOPE_ID = "others";

function courseScopeLinkClass(id: string, selectedId: string, ancestor = false) {
  if (ancestor) return "al-shell-nav-sublink al-shell-nav-sublink--open";
  return id === selectedId
    ? "al-shell-nav-sublink al-shell-nav-sublink--active"
    : "al-shell-nav-sublink";
}

function CoursesSubnav({
  scopes,
  selectedId,
  pick,
  mineSections,
  mineSectionId,
  pickMineSection,
  catalogInbox,
  proposalCounts,
}: {
  scopes: CoursePageScope[];
  selectedId: string;
  pick: (id: string) => void;
  mineSections?: CoursePageScopes["mineSections"];
  mineSectionId?: string | null;
  pickMineSection?: CoursePageScopes["pickMineSection"];
  catalogInbox?: CoursePageScopes["catalogInbox"];
  proposalCounts?: ProposalNavCounts;
}) {
  const catalog = scopes.find((scope) => scope.id === CATALOG_SCOPE_ID);
  const mine = scopes.find((scope) => scope.id === MY_COURSES_SCOPE_ID);
  const others = scopes.find((scope) => scope.id === OTHERS_SCOPE_ID);
  const schools = scopes.filter(
    (scope) =>
      scope.id !== CATALOG_SCOPE_ID &&
      scope.id !== MY_COURSES_SCOPE_ID &&
      scope.id !== OTHERS_SCOPE_ID,
  );
  const showMineSections = selectedId === MY_COURSES_SCOPE_ID && Boolean(mineSections?.length);
  const mineNestedActive = showMineSections && Boolean(mineSectionId);

  return (
    <ul className="al-shell-nav-sub" aria-label="Course lists">
      {catalog ? (
        <li>
          <button
            type="button"
            className={courseScopeLinkClass(catalog.id, selectedId)}
            aria-current={catalog.id === selectedId ? "true" : undefined}
            onClick={() => pick(catalog.id)}
          >
            Catalog (Templates)
          </button>
          {catalogInbox && catalogInbox.count > 0 ? (
            <ul className="al-shell-nav-sub al-shell-nav-sub--branch" aria-label="Catalog requests">
              <li>
                <button type="button" className="al-shell-nav-hint" onClick={catalogInbox.open}>
                  Requests ({catalogInbox.count})
                </button>
              </li>
            </ul>
          ) : null}
        </li>
      ) : null}
      {mine ? (
        <li>
          <button
            type="button"
            className={courseScopeLinkClass(mine.id, selectedId, mineNestedActive)}
            aria-current={mine.id === selectedId && !mineNestedActive ? "true" : undefined}
            onClick={() => pick(mine.id)}
          >
            {mine.name}
          </button>
          {showMineSections ? (
            <ul className="al-shell-nav-sub al-shell-nav-sub--branch" aria-label="My courses sections">
              {mineSections!.map((section) => (
                <li key={section.key}>
                  <button
                    type="button"
                    className={courseScopeLinkClass(section.key, mineSectionId || "")}
                    aria-current={section.key === mineSectionId ? "true" : undefined}
                    onClick={() => pickMineSection?.(section.key)}
                  >
                    {section.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </li>
      ) : null}
      {others ? (
        <li>
          <button
            type="button"
            className={courseScopeLinkClass(others.id, selectedId)}
            aria-current={others.id === selectedId ? "true" : undefined}
            onClick={() => pick(others.id)}
          >
            {others.name}
          </button>
        </li>
      ) : null}
      {schools.length ? (
        <li>
          <span className="al-shell-nav-group">By School</span>
          <ul className="al-shell-nav-sub al-shell-nav-sub--branch">
            {schools.map((school) => {
              const waiting = proposalCounts?.bySchool[school.id] || 0;
              return (
                <li key={school.id}>
                  <button
                    type="button"
                    className={`${courseScopeLinkClass(school.id, selectedId)}${waiting ? " al-proposal-nav-school" : ""}`}
                    aria-current={school.id === selectedId ? "true" : undefined}
                    title={waiting ? `${school.name}. ${waiting} course ${waiting === 1 ? "request" : "requests"} waiting` : school.name}
                    onClick={() => pick(school.id)}
                  >
                    {waiting ? <span className="al-proposal-nav-school-name">{school.name}</span> : school.name}
                    <ProposalNavCount count={waiting} />
                  </button>
                </li>
              );
            })}
          </ul>
        </li>
      ) : null}
    </ul>
  );
}

function SchoolsSubnav({
  schoolName,
  sections,
  sectionId,
  pick,
}: Pick<SchoolPageNav, "schoolName" | "sections" | "sectionId" | "pick">) {
  return (
    <ul className="al-shell-nav-sub al-shell-nav-sub--schools" aria-label="This school">
      <li>
        <button
          type="button"
          className="al-shell-nav-sublink al-shell-nav-sublink--open"
          title={schoolName}
          onClick={() => pick("details")}
        >
          {schoolName}
        </button>
        {sections.length ? (
          <ul className="al-shell-nav-sub al-shell-nav-sub--branch" aria-label="School sections">
            {sections.map((section) => (
              <li key={section.key}>
                <button
                  type="button"
                  className={courseScopeLinkClass(section.key, sectionId || "details")}
                  aria-current={section.key === (sectionId || "details") ? "true" : undefined}
                  onClick={() => pick(section.key)}
                >
                  {section.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </li>
    </ul>
  );
}

function ShellNavList({
  items,
  onNavigate,
  collapsed = false,
  proposalCounts,
  unreadCount = 0,
}: {
  items: NavItem[];
  onNavigate?: () => void;
  collapsed?: boolean;
  proposalCounts?: ProposalNavCounts;
  unreadCount?: number;
}) {
  const { pathname } = useLocation();
  const courseScopes = useCoursePageScopes();
  const schoolNav = useSchoolPageNav();
  const showCourseSub = Boolean(
    !collapsed &&
      pathname === "/courses" &&
      courseScopes &&
      (courseScopes.scopes.length > 1 ||
        Boolean(courseScopes.mineSections?.length) ||
        Boolean(courseScopes.catalogInbox && courseScopes.catalogInbox.count > 0)),
  );
  const showSchoolSub = Boolean(!collapsed && pathname.startsWith("/schools/") && schoolNav);

  return (
    <ul className="al-shell-nav-list">
      {items.map((item) => {
        const nestedActive = item.path !== "/" && pathname.startsWith(`${item.path}/`);
        const notice = item.id === "courses" ? proposalCounts?.total || 0 : item.id === "messages" ? unreadCount : 0;
        return (
          <li key={`${item.id}:${item.path}`}>
            <NavLink
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) => {
                const onPage = isActive || nestedActive;
                if (!onPage) return "al-shell-nav-link";
                if (item.id === "courses" && showCourseSub) {
                  return "al-shell-nav-link al-shell-nav-link--open";
                }
                if (item.id === "schools" && showSchoolSub) {
                  return "al-shell-nav-link al-shell-nav-link--open";
                }
                return "al-shell-nav-link al-shell-nav-link--active";
              }}
              onClick={onNavigate}
            >
              <span className="al-shell-nav-icon-wrap">
                <NavIcon name={item.id} />
                {collapsed ? <ProposalNavCount count={notice} variant="icon" /> : null}
              </span>
              <span className="al-shell-nav-label" title={item.label}>
                {item.label}
              </span>
              {!collapsed ? <ProposalNavCount count={notice} /> : null}
            </NavLink>
            {item.id === "courses" && showCourseSub && courseScopes ? (
              <CoursesSubnav
                scopes={courseScopes.scopes}
                selectedId={courseScopes.selectedId}
                pick={courseScopes.pick}
                mineSections={courseScopes.mineSections}
                mineSectionId={courseScopes.mineSectionId}
                pickMineSection={courseScopes.pickMineSection}
                catalogInbox={courseScopes.catalogInbox}
                proposalCounts={proposalCounts}
              />
            ) : null}
            {item.id === "schools" && showSchoolSub && schoolNav ? (
              <SchoolsSubnav
                schoolName={schoolNav.schoolName}
                sections={schoolNav.sections}
                sectionId={schoolNav.sectionId}
                pick={schoolNav.pick}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export default function AppShell() {
  const { loading, isAuthenticated, hasAccess, session, logout, nav } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const compact = useMediaQuery(COMPACT_QUERY);
  useKickHomeNotice();
  const [navOpen, setNavOpen] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);

  const grants = session?.acuityLearn;
  const homePath = grants?.defaultHomePath || "/";
  const links = isAuthenticated && hasAccess && nav.length ? nav : PUBLIC_NAV;
  const dockLinks = compact && links.length > DOCK_MAX ? links.slice(0, DOCK_MAX - 1) : links;
  const overflowLinks = compact && links.length > DOCK_MAX ? links.slice(DOCK_MAX - 1) : [];
  const proposalCounts = useProposalNavCounts(
    isAuthenticated && hasAccess && links.some((item) => item.id === "courses"),
    location.pathname,
  );
  const unreadCount = useUnreadNavCount(
    isAuthenticated && hasAccess && links.some((item) => item.id === "messages"),
    location.pathname,
  );

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname, compact]);

  const signOut = () => {
    setMoreOpen(false);
    logout();
    navigate("/");
  };

  if (loading) {
    return <div className="al-shell al-shell--loading" aria-hidden="true" />;
  }

  if (!isAuthenticated) {
    return (
      <div className="al-shell">
        <div className="al-shell-body">
          <Outlet />
        </div>
      </div>
    );
  }

  const signedIn = isAuthenticated && hasAccess;
  const collapsed = !compact && !navOpen;

  return (
    <div
      className={[
        "al-shell",
        compact ? "al-shell--compact" : "",
        collapsed ? "al-shell--nav-collapsed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!compact ? (
        <aside
          id="al-shell-sidebar"
          className={`al-shell-sidebar${collapsed ? " al-shell-sidebar--collapsed" : ""}`}
          aria-label="Main navigation"
          onDoubleClick={(event) => {
            const el = event.target instanceof Element ? event.target : null;
            if (!el) return;
            if (el.closest("button, input, select, textarea, label, .al-shell-nav a")) return;
            setNavOpen((open) => !open);
          }}
        >
          <div className="al-shell-brand-row">
            <div className="al-shell-brand">
              <Link to={homePath} className="al-shell-brand-mark" title="AcuityLearn" aria-label="AcuityLearn home">
                <img src="/brand/acuity-mark.png" alt="" />
              </Link>
              <span className="al-shell-brand-copy">
                <Link to={homePath} className="al-shell-brand-title">
                  AcuityLearn
                </Link>
                {signedIn ? (
                  <ContextSwitcher variant="brand" />
                ) : (
                  <span className="al-shell-brand-meta-line">Sign in to continue</span>
                )}
              </span>
            </div>
            <button
              type="button"
              className="al-shell-nav-toggle"
              aria-label={navOpen ? "Collapse navigation" : "Expand navigation"}
              aria-expanded={navOpen}
              title={navOpen ? "Collapse" : "Expand"}
              onClick={() => setNavOpen((open) => !open)}
            >
              {navOpen ? (
                <ChevronLeft strokeWidth={2} aria-hidden="true" />
              ) : (
                <ChevronRight strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </div>

          <nav className="al-shell-nav">
            <ShellNavList items={links} collapsed={collapsed} proposalCounts={proposalCounts} unreadCount={unreadCount} />
          </nav>

          {isAuthenticated ? (
            <div className="al-shell-sidebar-footer">
              {hasAccess ? <ContextSwitcher variant="shell" collapsed={collapsed} /> : null}
              <button type="button" className="al-shell-signout" onClick={signOut}>
                <span className="al-shell-nav-icon-wrap">
                  <LogOut className="al-shell-nav-icon" aria-hidden="true" strokeWidth={2} />
                </span>
                <span className="al-shell-signout-label">Sign out</span>
              </button>
            </div>
          ) : null}
        </aside>
      ) : (
        <nav className="al-shell-dock" aria-label="Main navigation">
          <div className="al-shell-dock-top">
            <div className="al-shell-dock-brand">
              <Link to={homePath} className="al-shell-brand-mark" title="AcuityLearn" aria-label="AcuityLearn home">
                <img src="/brand/acuity-mark.png" alt="" />
              </Link>
              <span className="al-shell-dock-brand-copy">
                <Link to={homePath} className="al-shell-brand-title">
                  AcuityLearn
                </Link>
                {signedIn ? <ContextSwitcher variant="brand" /> : null}
              </span>
            </div>
            {isAuthenticated ? (
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button type="button" className="al-shell-dock-signout" onClick={signOut}>
                  Sign out
                </button>
              </span>
            ) : null}
          </div>
          {hasAccess ? <ContextSwitcher variant="dock" /> : null}
          <ul className="al-shell-dock-list">
            {dockLinks.map((item) => {
              const nestedActive = item.path !== "/" && location.pathname.startsWith(`${item.path}/`);
              return (
                <li key={`${item.id}:${item.path}`}>
                  <NavLink
                    to={item.path}
                    end={item.path === "/"}
                    className={({ isActive }) =>
                      `al-shell-dock-link${isActive || nestedActive ? " al-shell-dock-link--active" : ""}`
                    }
                  >
                    <span className="al-proposal-nav-dock-icon">
                      <NavIcon name={item.id} />
                      <ProposalNavCount
                        count={item.id === "courses" ? proposalCounts.total : item.id === "messages" ? unreadCount : 0}
                        variant="icon"
                      />
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
            {overflowLinks.length ? (
              <li className="al-shell-dock-more">
                <button
                  type="button"
                  className={`al-shell-dock-link${moreOpen ? " al-shell-dock-link--active" : ""}`}
                  aria-expanded={moreOpen}
                  aria-haspopup="menu"
                  onClick={() => setMoreOpen((open) => !open)}
                >
                  <MoreHorizontal className="al-shell-nav-icon" aria-hidden="true" strokeWidth={2} />
                  <span>More</span>
                </button>
                {moreOpen ? (
                  <ul className="al-shell-dock-overflow" role="menu">
                    {overflowLinks.map((item) => (
                      <li key={`${item.id}:${item.path}`} role="none">
                        <NavLink
                          to={item.path}
                          role="menuitem"
                          className="al-shell-dock-overflow-link"
                          onClick={() => setMoreOpen(false)}
                        >
                          <NavIcon name={item.id} />
                          <span>
                            {item.label}
                            {item.id === "courses" && proposalCounts.total > 0 ? ` ${proposalCounts.total}` : ""}
                            {item.id === "messages" && unreadCount > 0 ? ` ${unreadCount}` : ""}
                          </span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ) : null}
          </ul>
        </nav>
      )}

      <div className="al-shell-body">
        <Outlet />
      </div>
    </div>
  );
}
